import React, { useCallback, useState } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { Dialog } from '@modernloop/shared/components';
import { Typography } from '@mui/material';

import ResetPng from 'src/assets/images/reset.png';

import { useActivityFeedRefetch } from 'src/hooks/useActivityFeedRefetch';
import useRefetchScheduleTaskQuery from 'src/hooks/useRefetchScheduleTaskQuery';

import updateCandidateAvailability from 'src/utils/api/candidate/updateCandidateAvailability';

type ResetAvailabilityModalProps = {
  applicationId: string;
  jobStageId: string;
  candidateTimezone: string;
  taskId?: string;
  onClose: () => void;
  onConfirm: () => void;
};

const ResetAvailabilityModal = ({
  applicationId,
  candidateTimezone,
  jobStageId,
  taskId,
  onClose,
  onConfirm,
}: ResetAvailabilityModalProps) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const refetchActivityFeed = useActivityFeedRefetch();
  const refetchScheduleTask = useRefetchScheduleTaskQuery();

  const handleResetAndContinue = useCallback(() => {
    setIsSubmitting(true);

    // eslint-disable-next-line promise/catch-or-return
    updateCandidateAvailability({
      applicationId,
      jobStageId,
      candidateTimezone,
      taskId,
      candidateAvailability: [],
    }) // eslint-disable-next-line promise/always-return
      .then(async () => {
        await Promise.all([refetchActivityFeed(), refetchScheduleTask()]);
        onConfirm();
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  }, [applicationId, candidateTimezone, jobStageId, onConfirm, refetchActivityFeed, refetchScheduleTask, taskId]);

  return (
    <Dialog
      open
      maxWidth="xs"
      decoration={
        <img
          src={ResetPng}
          alt={t('hiring-dashboard-candidates:candidates.resetAvailability.resetAlt')}
          height="64px"
          width="64px"
        />
      }
      title={t('hiring-dashboard-candidates:candidates.resetAvailability.title')}
      onClose={onClose}
      cancelOptions={{ label: t('hiring-dashboard-common:common.cancel'), isDisabled: isSubmitting, onClick: onClose }}
      submitOptions={{
        label: t('hiring-dashboard-candidates:candidates.resetAvailability.resetAndContinue'),
        isDisabled: isSubmitting,
        isLoading: isSubmitting,
        onClick: handleResetAndContinue,
      }}
    >
      <Typography>{t('hiring-dashboard-candidates:candidates.resetAvailability.body')}</Typography>
    </Dialog>
  );
};

export default ResetAvailabilityModal;
